import React from "react";
import { Link } from "react-router-dom";

// SEO + helpful content rendered below each tool. Keyword-rich but genuinely
// useful: intro, feature bullets, FAQ, and internal links to related tools.

const PRIVACY =
  "Everything runs locally in your browser — nothing you paste is uploaded, logged, or stored.";

// Canonical FAQ entries — kept verbatim-aligned with seo.go's faqLD so the
// FAQPage structured data matches the on-page text (a Google requirement).
const freeFAQ = ["formatter free", "Yes — it's completely free, with no sign-up and no limits."];

// Per-format extras (shown below the two canonical FAQs; visible-only is fine).
const FORMATTER_COPY = {
  json: {
    blurb:
      "Paste minified or messy JSON and get clean, indented output instantly. Sort keys, control indentation, and generate Go structs from your JSON in one click.",
    faqs: [
      ["Can it convert JSON to a Go struct?", "Yes — the struct view infers Go types (including nested structs and slices) from your JSON, with configurable tag casing."],
      ["Does it validate JSON?", "Invalid JSON is reported inline so you can fix syntax errors before formatting."],
    ],
  },
  xml: {
    blurb:
      "Pretty-print and indent XML, collapse empty tags, and strip insignificant whitespace. Generate Go structs from any XML document.",
    faqs: [
      ["Can it self-close empty tags?", "Yes — toggle “Self-close Empty Tags” to render <tag></tag> as <tag/>."],
      ["Can I turn XML into a Go struct?", "Yes, the struct view derives Go types from your XML element tree."],
    ],
  },
  html: {
    blurb:
      "Beautify and re-indent HTML using Prettier, with full control over print width, tab width, and whitespace sensitivity.",
    faqs: [
      ["Which engine formats the HTML?", "Prettier's HTML formatter, with its complete option set exposed in the panel."],
    ],
  },
  css: {
    blurb:
      "Format and tidy CSS, SCSS, and LESS with Prettier. Control indentation and quote style, or minify for production.",
    faqs: [
      ["Can it minify CSS?", "Yes — the Minify button strips comments and whitespace for a compact build-ready output."],
    ],
  },
  js: {
    blurb:
      "Format JavaScript with Prettier and the full set of options — semicolons, quotes, trailing commas, arrow parens, and more. Generate Go types from object literals.",
    faqs: [
      ["Which options are supported?", "Every Prettier option that applies to JavaScript is exposed in the options panel and updates the output live."],
    ],
  },
  jsx: {
    blurb:
      "Format React JSX with Prettier, including JSX-specific options, and minify with Babel + Terser — all in the browser.",
    faqs: [
      ["Can it minify JSX?", "Yes — JSX is transpiled with Babel and minified with Terser, entirely client-side."],
    ],
  },
  yml: {
    blurb:
      "Clean up and re-indent YAML, sort keys, control line width, and generate Go structs from your config.",
    faqs: [
      ["Can it sort keys?", "Yes — toggle key sorting to alphabetize your YAML recursively."],
    ],
  },
};

// Build a formatter's content block.
export function formatterContent(type, label) {
  const copy = FORMATTER_COPY[type] || {
    blurb: `Format and beautify ${label} in your browser. ${PRIVACY}`,
    faqs: [],
  };
  return {
    title: `About the ${label} Formatter`,
    intro: [
      copy.blurb,
      `The ${label} formatter is free, requires no sign-up, and runs entirely client-side. ${PRIVACY}`,
    ],
    features: [
      `Beautify and re-indent ${label} with configurable options`,
      "Minify for a compact, production-ready result",
      "Live updates as you change options — no round-trips",
      "100% private: your data never leaves your browser",
    ],
    // First two FAQs mirror the FAQPage JSON-LD exactly.
    faqs: [
      [`Is my ${label} data private?`, `Yes. ${PRIVACY}`],
      [`Is the ${label} formatter free?`, freeFAQ[1]],
      ...copy.faqs,
    ],
    related: [
      { to: `/converter/${type}-to-json`, label: `${label} to JSON` },
      { to: `/converter/${type}-to-yml`, label: `${label} to YAML` },
      { to: "/formatter/json", label: "JSON Formatter" },
      { to: "/formatter/yml", label: "YAML Formatter" },
    ],
  };
}

// Build a converter's content block.
export function converterContent(fromKey, fromLabel, toKey, toLabel) {
  return {
    title: `${fromLabel} to ${toLabel} Converter`,
    intro: [
      `Convert ${fromLabel} to ${toLabel} instantly, right in your browser. Paste your ${fromLabel} on the left and get ${toLabel} on the right as you type.`,
      `It's free, needs no sign-up, and is completely private. ${PRIVACY}`,
    ],
    features: [
      `Accurate ${fromLabel} → ${toLabel} conversion`,
      "Live output as you edit the source",
      "Copy or download the result in one click",
      "100% private: nothing is uploaded",
    ],
    // Mirrors the FAQPage JSON-LD exactly.
    faqs: [
      [`Is my ${fromLabel} uploaded to a server?`, `No. ${PRIVACY}`],
      [`How do I convert ${fromLabel} to ${toLabel}?`, `Paste your ${fromLabel} into the input editor and the ${toLabel} output is generated automatically as you type.`],
    ],
    related: [
      { to: `/converter/${toKey}-to-${fromKey}`, label: `${toLabel} to ${fromLabel}` },
      { to: `/formatter/${fromKey}`, label: `${fromLabel} Formatter` },
      { to: `/formatter/${toKey}`, label: `${toLabel} Formatter` },
    ],
  };
}

export default function ToolContent({ content }) {
  if (!content) return null;
  const { title, intro = [], features = [], faqs = [], related = [] } = content;
  return (
    <section className="tool-content">
      <h2>{title}</h2>
      {intro.map((p, i) => (
        <p key={i}>{p}</p>
      ))}

      {features.length > 0 && (
        <>
          <h3>Features</h3>
          <ul className="tc-features">
            {features.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </>
      )}

      {faqs.length > 0 && (
        <>
          <h3>Frequently asked questions</h3>
          <dl className="tc-faq">
            {faqs.map(([q, a], i) => (
              <React.Fragment key={i}>
                <dt>{q}</dt>
                <dd>{a}</dd>
              </React.Fragment>
            ))}
          </dl>
        </>
      )}

      {related.length > 0 && (
        <>
          <h3>Related tools</h3>
          <ul className="tc-related">
            {related.map((r, i) => (
              <li key={i}>
                <Link to={r.to}>{r.label}</Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
